import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // send/receive the httpOnly auth cookie
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth is carried by an httpOnly cookie (set by the backend on login) and sent
// automatically because of `withCredentials: true`. We keep a lightweight
// header fallback only if a token happens to be present (legacy/no-cookie
// environments); the JWT is no longer persisted in localStorage by the app.
api.interceptors.request.use((config) => {
  const token =
    typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem('auth_token')
      : null;
  if (token && !config.headers?.Authorization) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
  }
  return config;
});

// Master Data APIs
export const masterDataAPI = {
  // Languages
  getLanguages: () => api.get('/master-data/languages'),
  createLanguage: (data) => api.post('/master-data/languages', data),
  updateLanguage: (code, data) => api.put(`/master-data/languages/${code}`, data),
  deleteLanguage: (code) => api.delete(`/master-data/languages/${code}`),

  // Countries
  getCountries: () => api.get('/master-data/countries'),
  createCountry: (data) => api.post('/master-data/countries', data),
  updateCountry: (code, data) => api.put(`/master-data/countries/${code}`, data),
  deleteCountry: (code) => api.delete(`/master-data/countries/${code}`),

  // Currencies
  getCurrencies: () => api.get('/master-data/currencies'),
  createCurrency: (data) => api.post('/master-data/currencies', data),
  updateCurrency: (code, data) => api.put(`/master-data/currencies/${code}`, data),
  deleteCurrency: (code) => api.delete(`/master-data/currencies/${code}`),
};

// Company APIs
export const companyAPI = {
  getCompanies: () => api.get('/company/companies'),
  createCompany: (data) => api.post('/company/companies', data),
  updateCompany: (id, data) => api.put(`/company/companies/${id}`, data),
  deleteCompany: (id) => api.delete(`/company/companies/${id}`),

  getCompanyCodes: () => api.get('/company/company-codes'),
  createCompanyCode: (data) => api.post('/company/company-codes', data),
  updateCompanyCode: (id, data) => api.put(`/company/company-codes/${id}`, data),
  deleteCompanyCode: (id) => api.delete(`/company/company-codes/${id}`),
};

// Sales & Distribution APIs
export const salesDistributionAPI = {
  // Sales Organizations
  getSalesOrganizations: () => api.get('/sales-distribution/sales-organizations'),
  createSalesOrganization: (data) => api.post('/sales-distribution/sales-organizations', data),
  updateSalesOrganization: (id, data) => api.put(`/sales-distribution/sales-organizations/${id}`, data),
  deleteSalesOrganization: (id) => api.delete(`/sales-distribution/sales-organizations/${id}`),

  // Sales Groups
  getSalesGroups: () => api.get('/sales-distribution/sales-groups'),
  createSalesGroup: (data) => api.post('/sales-distribution/sales-groups', data),
  updateSalesGroup: (id, data) => api.put(`/sales-distribution/sales-groups/${id}`, data),
  deleteSalesGroup: (id) => api.delete(`/sales-distribution/sales-groups/${id}`),

  // Sales Offices
  getSalesOffices: () => api.get('/sales-distribution/sales-offices'),
  createSalesOffice: (data) => api.post('/sales-distribution/sales-offices', data),
  updateSalesOffice: (id, data) => api.put(`/sales-distribution/sales-offices/${id}`, data),
  deleteSalesOffice: (id) => api.delete(`/sales-distribution/sales-offices/${id}`),

  // Distribution Channels
  getDistributionChannels: () => api.get('/sales-distribution/distribution-channels'),
  createDistributionChannel: (data) => api.post('/sales-distribution/distribution-channels', data),
  updateDistributionChannel: (id, data) => api.put(`/sales-distribution/distribution-channels/${id}`, data),
  deleteDistributionChannel: (id) => api.delete(`/sales-distribution/distribution-channels/${id}`),

  // Divisions
  getDivisions: () => api.get('/sales-distribution/divisions'),
  createDivision: (data) => api.post('/sales-distribution/divisions', data),
  updateDivision: (id, data) => api.put(`/sales-distribution/divisions/${id}`, data),
  deleteDivision: (id) => api.delete(`/sales-distribution/divisions/${id}`),

  // Sales Areas
  getSalesAreas: () => api.get('/sales-distribution/sales-areas'),
  createSalesArea: (data) => api.post('/sales-distribution/sales-areas', data),
  updateSalesArea: (id, data) => api.put(`/sales-distribution/sales-areas/${id}`, data),
  deleteSalesArea: (id) => api.delete(`/sales-distribution/sales-areas/${id}`),

  // Departure Zones
  getDepartureZones: () => api.get('/sales-distribution/departure-zones'),
  createDepartureZone: (data) => api.post('/sales-distribution/departure-zones', data),
  updateDepartureZone: (countryKey, zoneId, data) => api.put(`/sales-distribution/departure-zones/${countryKey}/${zoneId}`, data),
  deleteDepartureZone: (countryKey, zoneId) => api.delete(`/sales-distribution/departure-zones/${countryKey}/${zoneId}`),

  // Shipping Points
  getShippingPoints: () => api.get('/sales-distribution/shipping-points'),
  createShippingPoint: (data) => api.post('/sales-distribution/shipping-points', data),
  updateShippingPoint: (id, data) => api.put(`/sales-distribution/shipping-points/${id}`, data),
  deleteShippingPoint: (id) => api.delete(`/sales-distribution/shipping-points/${id}`),

  // Shipping Point to Plant Assignments
  getShippingPointPlantAssignments: (params) => api.get('/sales-distribution/shipping-point-plant-assignments', { params }),
  createShippingPointPlantAssignment: (data) => api.post('/sales-distribution/shipping-point-plant-assignments', data),
  deleteShippingPointPlantAssignment: (shippingPointId, plantId) => api.delete(`/sales-distribution/shipping-point-plant-assignments/${shippingPointId}/${plantId}`),

  // Sales Org + Channel to Plant Assignments
  getSalesOrgChannelPlantAssignments: (params) => api.get('/sales-distribution/sales-org-channel-plant-assignments', { params }),
  createSalesOrgChannelPlantAssignment: (data) => api.post('/sales-distribution/sales-org-channel-plant-assignments', data),
  deleteSalesOrgChannelPlantAssignment: (salesOrgId, channelId, plantId) => api.delete(`/sales-distribution/sales-org-channel-plant-assignments/${salesOrgId}/${channelId}/${plantId}`),

  // Sales Area to Credit Control Area Assignments
  getSalesAreaCreditControlAssignments: (params) => api.get('/sales-distribution/sales-area-credit-control-assignments', { params }),
  createSalesAreaCreditControlAssignment: (data) => api.post('/sales-distribution/sales-area-credit-control-assignments', data),
  deleteSalesAreaCreditControlAssignment: (salesOrgId, channelId, divisionId) => api.delete(`/sales-distribution/sales-area-credit-control-assignments/${salesOrgId}/${channelId}/${divisionId}`),
};

// Assignment APIs
export const assignmentsAPI = {
  // Sales Org to Distribution Channel
  getSalesOrgDistChannels: () => api.get('/assignments/sales-org-dist-channel'),
  createSalesOrgDistChannel: (data) => api.post('/assignments/sales-org-dist-channel', data),
  getDistChannelsBySalesOrg: (salesOrgId) => api.get(`/assignments/sales-org-dist-channel/by-sales-org/${salesOrgId}`),
  deleteSalesOrgDistChannel: (id) => api.delete(`/assignments/sales-org-dist-channel/${id}`),

  // Sales Org to Division
  getSalesOrgDivisions: () => api.get('/assignments/sales-org-division'),
  createSalesOrgDivision: (data) => api.post('/assignments/sales-org-division', data),
  getDivisionsBySalesOrg: (salesOrgId) => api.get(`/assignments/sales-org-division/by-sales-org/${salesOrgId}`),
  deleteSalesOrgDivision: (id) => api.delete(`/assignments/sales-org-division/${id}`),

  // Sales Area to Sales Office
  getSalesAreaOffices: () => api.get('/assignments/sales-area-office'),
  createSalesAreaOffice: (data) => api.post('/assignments/sales-area-office', data),
  getOfficesBySalesArea: (salesAreaId) => api.get(`/assignments/sales-area-office/by-sales-area/${salesAreaId}`),
  deleteSalesAreaOffice: (id) => api.delete(`/assignments/sales-area-office/${id}`),

  // Sales Office to Sales Group
  getSalesOfficeGroups: () => api.get('/assignments/sales-office-group'),
  createSalesOfficeGroup: (data) => api.post('/assignments/sales-office-group', data),
  getGroupsBySalesOffice: (salesOfficeId) => api.get(`/assignments/sales-office-group/by-sales-office/${salesOfficeId}`),
  deleteSalesOfficeGroup: (id) => api.delete(`/assignments/sales-office-group/${id}`),
};

// Bulk Upload APIs
export const bulkUploadAPI = {
  downloadTemplate: (entityType) => {
    return api.get(`/bulk-upload/template/${entityType}`, {
      responseType: 'blob'
    });
  },
  uploadSalesOrganizations: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bulk-upload/upload/sales_organizations', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Finance APIs
export const financeAPI = {
  // Credit Control Areas
  getCreditControlAreas: () => api.get('/finance/credit-control-areas'),
  createCreditControlArea: (data) => api.post('/finance/credit-control-areas', data),
  updateCreditControlArea: (id, data) => api.put(`/finance/credit-control-areas/${id}`, data),
  deleteCreditControlArea: (id) => api.delete(`/finance/credit-control-areas/${id}`),

  // Business Areas
  getBusinessAreas: () => api.get('/finance/business-areas'),
  createBusinessArea: (data) => api.post('/finance/business-areas', data),
  updateBusinessArea: (id, data) => api.put(`/finance/business-areas/${id}`, data),
  deleteBusinessArea: (id) => api.delete(`/finance/business-areas/${id}`),

  // Consolidated Business Areas
  getConsolidatedBusinessAreas: () => api.get('/finance/consolidated-business-areas'),
  createConsolidatedBusinessArea: (data) => api.post('/finance/consolidated-business-areas', data),
  updateConsolidatedBusinessArea: (id, data) => api.put(`/finance/consolidated-business-areas/${id}`, data),
  deleteConsolidatedBusinessArea: (id) => api.delete(`/finance/consolidated-business-areas/${id}`),

  // Functional Areas
  getFunctionalAreas: () => api.get('/finance/functional-areas'),
  createFunctionalArea: (data) => api.post('/finance/functional-areas', data),
  updateFunctionalArea: (id, data) => api.put(`/finance/functional-areas/${id}`, data),
  deleteFunctionalArea: (id) => api.delete(`/finance/functional-areas/${id}`),

  // Segments
  getSegments: () => api.get('/finance/segments'),
  createSegment: (data) => api.post('/finance/segments', data),
  updateSegment: (id, data) => api.put(`/finance/segments/${id}`, data),
  deleteSegment: (id) => api.delete(`/finance/segments/${id}`),

  // Chart of Accounts
  // Chart of Accounts
  getChartOfAccounts: () => api.get('/finance/chart-of-accounts'),
  createChartOfAccounts: (data) => api.post('/finance/chart-of-accounts', data),
  updateChartOfAccounts: (id, data) => api.put(`/finance/chart-of-accounts/${id}`, data),
  deleteChartOfAccounts: (id) => api.delete(`/finance/chart-of-accounts/${id}`),
  
  // GL Accounts within COA
  getGLAccountsInCOA: (coaId) => api.get(`/finance/chart-of-accounts/${coaId}/accounts`),
  addGLAccountToCOA: (coaId, data) => api.post(`/finance/chart-of-accounts/${coaId}/accounts`, data),
  updateGLAccount: (coaId, accountNumber, data) => api.put(`/finance/chart-of-accounts/${coaId}/accounts/${accountNumber}`, data),
  deleteGLAccount: (coaId, accountNumber) => api.delete(`/finance/chart-of-accounts/${coaId}/accounts/${accountNumber}`),

  // Fiscal Year Variants
  getFiscalYearVariants: () => api.get('/finance/fiscal-year-variants'),
  createFiscalYearVariant: (data) => api.post('/finance/fiscal-year-variants', data),
  updateFiscalYearVariant: (id, data) => api.put(`/finance/fiscal-year-variants/${id}`, data),
  deleteFiscalYearVariant: (id) => api.delete(`/finance/fiscal-year-variants/${id}`),
};

// Finance Assignments APIs
export const financeAssignmentsAPI = {
  // Company Code to Credit Control Area
  getCompanyCodeCCAAssignments: (companyCodeId, ccaId) => {
    const params = new URLSearchParams();
    if (companyCodeId) params.append('company_code_id', companyCodeId);
    if (ccaId) params.append('credit_control_area', ccaId);
    return api.get(`/finance-assignments/company-code-cca?${params.toString()}`);
  },
  createCompanyCodeCCAAssignment: (data) => api.post('/finance-assignments/company-code-cca', data),
  deleteCompanyCodeCCAAssignment: (id) => api.delete(`/finance-assignments/company-code-cca/${id}`),

  // Business Area to Consolidated Business Area
  getBusinessAreaCBAAssignments: (businessAreaId, cbaId) => {
    const params = new URLSearchParams();
    if (businessAreaId) params.append('business_area', businessAreaId);
    if (cbaId) params.append('consolidated_business_area', cbaId);
    return api.get(`/finance-assignments/business-area-cba?${params.toString()}`);
  },
  createBusinessAreaCBAAssignment: (data) => api.post('/finance-assignments/business-area-cba', data),
  deleteBusinessAreaCBAAssignment: (id) => api.delete(`/finance-assignments/business-area-cba/${id}`),

  // Company Code to Functional Area
  getCompanyCodeFAAssignments: (companyCodeId, faId) => {
    const params = new URLSearchParams();
    if (companyCodeId) params.append('company_code_id', companyCodeId);
    if (faId) params.append('functional_area', faId);
    return api.get(`/finance-assignments/company-code-fa?${params.toString()}`);
  },
  createCompanyCodeFAAssignment: (data) => api.post('/finance-assignments/company-code-fa', data),
  deleteCompanyCodeFAAssignment: (id) => api.delete(`/finance-assignments/company-code-fa/${id}`),

  // Company Code to COA
  getCompanyCodeCOAAssignments: () => api.get('/finance-assignments/company-code-coa'),
  createCompanyCodeCOAAssignment: (data) => api.post('/finance-assignments/company-code-coa', data),
  updateCompanyCodeCOAAssignment: (id, data) => api.put(`/finance-assignments/company-code-coa/${id}`, data),
  deleteCompanyCodeCOAAssignment: (id) => api.delete(`/finance-assignments/company-code-coa/${id}`),

  // Company Code to FYV
  getCompanyCodeFYVAssignments: () => api.get('/finance-assignments/company-code-fyv'),
  createCompanyCodeFYVAssignment: (data) => api.post('/finance-assignments/company-code-fyv', data),
  updateCompanyCodeFYVAssignment: (id, data) => api.put(`/finance-assignments/company-code-fyv/${id}`, data),
  deleteCompanyCodeFYVAssignment: (id) => api.delete(`/finance-assignments/company-code-fyv/${id}`),

  // GL Account Extensions
  getGLAccountExtensions: (companyCodeId, coaId, isActive) => {
    const params = new URLSearchParams();
    if (companyCodeId) params.append('company_code_id', companyCodeId);
    if (coaId) params.append('coa_id', coaId);
    if (isActive !== undefined) params.append('is_active', isActive);
    return api.get(`/finance-assignments/gl-account-extensions?${params.toString()}`);
  },
  getGLAccountsForCompanyCode: (companyCodeId) => 
    api.get(`/finance-assignments/gl-account-extensions/company-code/${companyCodeId}`),
  createGLAccountExtension: (data) => api.post('/finance-assignments/gl-account-extensions', data),
  updateGLAccountExtension: (id, data) => api.put(`/finance-assignments/gl-account-extensions/${id}`, data),
  deleteGLAccountExtension: (id) => api.delete(`/finance-assignments/gl-account-extensions/${id}`),
};

// Controlling APIs
export const controllingAPI = {
  // Operating Concerns
  getOperatingConcerns: () => api.get('/controlling/operating-concerns'),
  createOperatingConcern: (data) => api.post('/controlling/operating-concerns', data),
  updateOperatingConcern: (id, data) => api.put(`/controlling/operating-concerns/${id}`, data),
  deleteOperatingConcern: (id) => api.delete(`/controlling/operating-concerns/${id}`),

  // Controlling Areas
  getControllingAreas: () => api.get('/controlling/controlling-areas'),
  createControllingArea: (data) => api.post('/controlling/controlling-areas', data),
  updateControllingArea: (id, data) => api.put(`/controlling/controlling-areas/${id}`, data),
  deleteControllingArea: (id) => api.delete(`/controlling/controlling-areas/${id}`),

  // Cost Center Groups
  getCostCenterGroups: (controllingAreaId) => {
    const params = controllingAreaId ? `?controlling_area_id=${controllingAreaId}` : '';
    return api.get(`/controlling/cost-center-groups${params}`);
  },
  getCostCenterGroup: (controllingAreaId, groupId) => 
    api.get(`/controlling/cost-center-groups/${controllingAreaId}/${groupId}`),
  getRootGroupForCA: (controllingAreaId) =>
    api.get(`/controlling/cost-center-groups/${controllingAreaId}/root`),
  createCostCenterGroup: (data) => api.post('/controlling/cost-center-groups', data),
  updateCostCenterGroup: (controllingAreaId, groupId, data) => 
    api.put(`/controlling/cost-center-groups/${controllingAreaId}/${groupId}`, data),
  deleteCostCenterGroup: (controllingAreaId, groupId) => 
    api.delete(`/controlling/cost-center-groups/${controllingAreaId}/${groupId}`),

  // Cost Center Hierarchy (old - keeping for backward compatibility)
  getCostCenterHierarchyNodes: (controllingAreaId) => {
    const params = controllingAreaId ? `?controlling_area_id=${controllingAreaId}` : '';
    return api.get(`/controlling/cost-center-hierarchy${params}`);
  },
  createCostCenterHierarchyNode: (data) => api.post('/controlling/cost-center-hierarchy', data),
  updateCostCenterHierarchyNode: (id, data) => api.put(`/controlling/cost-center-hierarchy/${id}`, data),
  deleteCostCenterHierarchyNode: (id) => api.delete(`/controlling/cost-center-hierarchy/${id}`),

  // Cost Centers
  getCostCenters: (controllingAreaId) => {
    const params = controllingAreaId ? `?controlling_area_id=${controllingAreaId}` : '';
    return api.get(`/controlling/cost-centers${params}`);
  },
  getCostCenter: (controllingAreaId, costCenterId) =>
    api.get(`/controlling/cost-centers/${controllingAreaId}/${costCenterId}`),
  createCostCenter: (data) => api.post('/controlling/cost-centers', data),
  updateCostCenter: (controllingAreaId, costCenterId, data) => 
    api.put(`/controlling/cost-centers/${controllingAreaId}/${costCenterId}`, data),
  deleteCostCenter: (controllingAreaId, costCenterId) => 
    api.delete(`/controlling/cost-centers/${controllingAreaId}/${costCenterId}`),

  // Profit Center Groups
  getProfitCenterGroups: (controllingAreaId) => {
    const params = controllingAreaId ? `?controlling_area_id=${controllingAreaId}` : '';
    return api.get(`/controlling/profit-center-groups${params}`);
  },
  getProfitCenterGroup: (controllingAreaId, groupId) => 
    api.get(`/controlling/profit-center-groups/${controllingAreaId}/${groupId}`),
  getRootPCGroupForCA: (controllingAreaId) =>
    api.get(`/controlling/profit-center-groups/${controllingAreaId}/root`),
  createProfitCenterGroup: (data) => api.post('/controlling/profit-center-groups', data),
  updateProfitCenterGroup: (controllingAreaId, groupId, data) => 
    api.put(`/controlling/profit-center-groups/${controllingAreaId}/${groupId}`, data),
  deleteProfitCenterGroup: (controllingAreaId, groupId) => 
    api.delete(`/controlling/profit-center-groups/${controllingAreaId}/${groupId}`),

  // Profit Centers
  getProfitCenters: (controllingAreaId) => {
    const params = controllingAreaId ? `?controlling_area_id=${controllingAreaId}` : '';
    return api.get(`/controlling/profit-centers${params}`);
  },
  getProfitCenter: (controllingAreaId, profitCenterId) =>
    api.get(`/controlling/profit-centers/${controllingAreaId}/${profitCenterId}`),
  createProfitCenter: (data) => api.post('/controlling/profit-centers', data),
  updateProfitCenter: (controllingAreaId, profitCenterId, data) => 
    api.put(`/controlling/profit-centers/${controllingAreaId}/${profitCenterId}`, data),
  deleteProfitCenter: (controllingAreaId, profitCenterId) => 
    api.delete(`/controlling/profit-centers/${controllingAreaId}/${profitCenterId}`),

  // Internal Orders
  getInternalOrders: (controllingAreaId) => {
    const params = controllingAreaId ? `?controlling_area_id=${controllingAreaId}` : '';
    return api.get(`/controlling/internal-orders${params}`);
  },
  createInternalOrder: (data) => api.post('/controlling/internal-orders', data),
  updateInternalOrder: (id, data) => api.put(`/controlling/internal-orders/${id}`, data),
  deleteInternalOrder: (id) => api.delete(`/controlling/internal-orders/${id}`),

  // Statistical Key Figures
  getStatisticalKeyFigures: (controllingAreaId) => {
    const params = controllingAreaId ? `?controlling_area_id=${controllingAreaId}` : '';
    return api.get(`/controlling/statistical-key-figures${params}`);
  },
  createStatisticalKeyFigure: (data) => api.post('/controlling/statistical-key-figures', data),
  updateStatisticalKeyFigure: (id, data) => api.put(`/controlling/statistical-key-figures/${id}`, data),
  deleteStatisticalKeyFigure: (id) => api.delete(`/controlling/statistical-key-figures/${id}`),

  // Cost Elements
  getCostElements: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.controlling_area_id) queryParams.append('controlling_area_id', params.controlling_area_id);
    if (params.category) queryParams.append('category', params.category);
    if (params.cost_element_type) queryParams.append('cost_element_type', params.cost_element_type);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    return api.get(`/controlling/cost-elements?${queryParams.toString()}`);
  },
  getCostElement: (id) => api.get(`/controlling/cost-elements/${id}`),
  createCostElement: (data) => api.post('/controlling/cost-elements', data),
  updateCostElement: (id, data) => api.put(`/controlling/cost-elements/${id}`, data),
  seedDefaultCostElements: (controllingAreaId = '1000') => 
    api.post(`/controlling/cost-elements/seed-defaults?controlling_area_id=${controllingAreaId}`),

  // Cost Element Groups
  getCostElementGroups: (controllingAreaId) => {
    const params = controllingAreaId ? `?controlling_area_id=${controllingAreaId}` : '';
    return api.get(`/controlling/cost-element-groups${params}`);
  },
  getCostElementGroup: (controllingAreaId, groupId) => 
    api.get(`/controlling/cost-element-groups/${controllingAreaId}/${groupId}`),
  createCostElementGroup: (data) => api.post('/controlling/cost-element-groups', data),
  updateCostElementGroup: (controllingAreaId, groupId, data) => 
    api.put(`/controlling/cost-element-groups/${controllingAreaId}/${groupId}`, data),
  deleteCostElementGroup: (controllingAreaId, groupId) => 
    api.delete(`/controlling/cost-element-groups/${controllingAreaId}/${groupId}`),
};

// Controlling Assignments APIs
export const controllingAssignmentsAPI = {
  // Controlling Area to Company Code
  getControllingAreaCompanyCodeAssignments: (controllingAreaId, companyCodeId) => {
    const params = new URLSearchParams();
    if (controllingAreaId) params.append('controlling_area_id', controllingAreaId);
    if (companyCodeId) params.append('company_code_id', companyCodeId);
    return api.get(`/controlling-assignments/controlling-area-company-code?${params.toString()}`);
  },
  createControllingAreaCompanyCodeAssignment: (data) => api.post('/controlling-assignments/controlling-area-company-code', data),
  deleteControllingAreaCompanyCodeAssignment: (id) => api.delete(`/controlling-assignments/controlling-area-company-code/${id}`),
  
  // Profit Center to Company Code (M:1)
  getProfitCenterCompanyCodeAssignments: (profitCenterId, companyCodeId) => {
    const params = new URLSearchParams();
    if (profitCenterId) params.append('profit_center_id', profitCenterId);
    if (companyCodeId) params.append('company_code_id', companyCodeId);
    return api.get(`/controlling-assignments/profit-center-company-code?${params.toString()}`);
  },
  createProfitCenterCompanyCodeAssignment: (data) => api.post('/controlling-assignments/profit-center-company-code', data),
  deleteProfitCenterCompanyCodeAssignment: (id) => api.delete(`/controlling-assignments/profit-center-company-code/${id}`),
  
  // Cost Center to Profit Center (1:1)
  getCostCenterProfitCenterAssignments: (costCenterId, profitCenterId) => {
    const params = new URLSearchParams();
    if (costCenterId) params.append('cost_center_id', costCenterId);
    if (profitCenterId) params.append('profit_center_id', profitCenterId);
    return api.get(`/controlling-assignments/cost-center-profit-center?${params.toString()}`);
  },
  createCostCenterProfitCenterAssignment: (data) => api.post('/controlling-assignments/cost-center-profit-center', data),
  updateCostCenterProfitCenterAssignment: (id, newProfitCenterId) => 
    api.put(`/controlling-assignments/cost-center-profit-center/${id}?new_profit_center_id=${newProfitCenterId}`),
  deleteCostCenterProfitCenterAssignment: (id) => api.delete(`/controlling-assignments/cost-center-profit-center/${id}`),
};

// Material Management APIs
export const materialManagementAPI = {
  // Holiday Calendars
  getHolidayCalendars: () => api.get('/material-management/holiday-calendars'),
  getHolidayCalendar: (id) => api.get(`/material-management/holiday-calendars/${id}`),
  createHolidayCalendar: (data) => api.post('/material-management/holiday-calendars', data),
  updateHolidayCalendar: (id, data) => api.put(`/material-management/holiday-calendars/${id}`, data),
  deleteHolidayCalendar: (id) => api.delete(`/material-management/holiday-calendars/${id}`),
  
  // Factory Calendars
  getFactoryCalendars: () => api.get('/material-management/factory-calendars'),
  getFactoryCalendar: (id) => api.get(`/material-management/factory-calendars/${id}`),
  createFactoryCalendar: (data) => api.post('/material-management/factory-calendars', data),
  updateFactoryCalendar: (id, data) => api.put(`/material-management/factory-calendars/${id}`, data),
  deleteFactoryCalendar: (id) => api.delete(`/material-management/factory-calendars/${id}`),
  
  // Plants
  getPlants: () => api.get('/material-management/plants'),
  getPlant: (id) => api.get(`/material-management/plants/${id}`),
  createPlant: (data) => api.post('/material-management/plants', data),
  updatePlant: (id, data) => api.put(`/material-management/plants/${id}`, data),
  deletePlant: (id) => api.delete(`/material-management/plants/${id}`),
  
  // Storage Locations
  getStorageLocations: (plantId) => {
    const params = plantId ? `?plant_id=${plantId}` : '';
    return api.get(`/material-management/storage-locations${params}`);
  },
  getStorageLocation: (plantId, locationId) => api.get(`/material-management/storage-locations/${plantId}/${locationId}`),
  createStorageLocation: (data) => api.post('/material-management/storage-locations', data),
  updateStorageLocation: (plantId, locationId, data) => api.put(`/material-management/storage-locations/${plantId}/${locationId}`, data),
  deleteStorageLocation: (plantId, locationId) => api.delete(`/material-management/storage-locations/${plantId}/${locationId}`),
  
  // Purchasing Organizations
  getPurchasingOrgs: () => api.get('/purchasing-management/purchasing-organizations'),
  createPurchasingOrg: (data) => api.post('/purchasing-management/purchasing-organizations', data),
  updatePurchasingOrg: (id, data) => api.put(`/purchasing-management/purchasing-organizations/${id}`, data),
  deletePurchasingOrg: (id) => api.delete(`/purchasing-management/purchasing-organizations/${id}`),
  
  // Purchasing Groups
  getPurchasingGroups: () => api.get('/purchasing-management/purchasing-groups'),
  createPurchasingGroup: (data) => api.post('/purchasing-management/purchasing-groups', data),
  updatePurchasingGroup: (id, data) => api.put(`/purchasing-management/purchasing-groups/${id}`, data),
  deletePurchasingGroup: (id) => api.delete(`/purchasing-management/purchasing-groups/${id}`),
};


// Extended Warehouse Management (EWM) APIs
export const ewmAPI = {
  // Warehouses
  getWarehouses: () => api.get('/ewm/warehouses'),
  getWarehouse: (warehouseNumber) => api.get(`/ewm/warehouses/${warehouseNumber}`),
  createWarehouse: (data) => api.post('/ewm/warehouses', data),
  updateWarehouse: (warehouseNumber, data) => api.put(`/ewm/warehouses/${warehouseNumber}`, data),
  deleteWarehouse: (warehouseNumber) => api.delete(`/ewm/warehouses/${warehouseNumber}`),
  
  // Storage Types
  getStorageTypes: (warehouseNumber) => api.get('/ewm/storage-types', { params: { warehouse_number: warehouseNumber } }),
  getStorageType: (storageTypeId) => api.get(`/ewm/storage-types/${storageTypeId}`),
  createStorageType: (data) => api.post('/ewm/storage-types', data),
  updateStorageType: (storageTypeId, data) => api.put(`/ewm/storage-types/${storageTypeId}`, data),
  deleteStorageType: (storageTypeId) => api.delete(`/ewm/storage-types/${storageTypeId}`),
  
  // Storage Sections
  getStorageSections: (storageTypeId) => api.get('/ewm/storage-sections', { params: { storage_type_id: storageTypeId } }),
  getStorageSection: (storageSectionId) => api.get(`/ewm/storage-sections/${storageSectionId}`),
  createStorageSection: (data) => api.post('/ewm/storage-sections', data),
  updateStorageSection: (storageSectionId, data) => api.put(`/ewm/storage-sections/${storageSectionId}`, data),
  deleteStorageSection: (storageSectionId) => api.delete(`/ewm/storage-sections/${storageSectionId}`),
  
  // Storage Bins
  getStorageBins: (storageSectionId) => api.get('/ewm/storage-bins', { params: { storage_section_id: storageSectionId } }),
  getStorageBin: (storageBinId) => api.get(`/ewm/storage-bins/${storageBinId}`),
  createStorageBin: (data) => api.post('/ewm/storage-bins', data),
  updateStorageBin: (storageBinId, data) => api.put(`/ewm/storage-bins/${storageBinId}`, data),
  deleteStorageBin: (storageBinId) => api.delete(`/ewm/storage-bins/${storageBinId}`),
  
  // EWM Assignments
  getWarehousePlantStorageLocationAssignments: (warehouseNumber) => 
    api.get('/ewm/assignments/warehouse-plant-storage-location', { params: { warehouse_number: warehouseNumber } }),
  createWarehousePlantStorageLocationAssignment: (data) => 
    api.post('/ewm/assignments/warehouse-plant-storage-location', data),
  deleteWarehousePlantStorageLocationAssignment: (warehouseNumber, plantId, storageLocationId) => 
    api.delete(`/ewm/assignments/warehouse-plant-storage-location/${warehouseNumber}/${plantId}/${storageLocationId}`),
  
  // Storage Type to Warehouse
  assignStorageTypeToWarehouse: (storageTypeId, warehouseNumber) => 
    api.put(`/ewm/assignments/storage-type-warehouse/${storageTypeId}/${warehouseNumber}`),
  unassignStorageTypeFromWarehouse: (storageTypeId) => 
    api.delete(`/ewm/assignments/storage-type-warehouse/${storageTypeId}`),
  
  // Storage Section to Storage Type
  assignStorageSectionToType: (storageSectionId, storageTypeId) => 
    api.put(`/ewm/assignments/storage-section-type/${storageSectionId}/${storageTypeId}`),
  unassignStorageSectionFromType: (storageSectionId) => 
    api.delete(`/ewm/assignments/storage-section-type/${storageSectionId}`),
  
  // Storage Bin to Storage Type
  assignStorageBinToType: (storageBinId, storageTypeId) => 
    api.put(`/ewm/assignments/storage-bin-type/${storageBinId}/${storageTypeId}`),
  unassignStorageBinFromType: (storageBinId) => 
    api.delete(`/ewm/assignments/storage-bin-type/${storageBinId}`),
  
  // Storage Bin to Storage Section
  assignStorageBinToSection: (storageBinId, storageSectionId) => 
    api.put(`/ewm/assignments/storage-bin-section/${storageBinId}/${storageSectionId}`),
  unassignStorageBinFromSection: (storageBinId) => 
    api.delete(`/ewm/assignments/storage-bin-section/${storageBinId}`),
};

// HCM APIs
export const hcmAPI = {
  // Employees
  getEmployees: (params = {}) => api.get('/hcm/employees', { params }),
  getEmployee: (employeeId) => api.get(`/hcm/employees/${employeeId}`),
  createEmployee: (data) => api.post('/hcm/employees', data),
  updateEmployee: (employeeId, data) => api.put(`/hcm/employees/${employeeId}`, data),
  
  // Personnel Areas
  getPersonnelAreas: (companyCodeId, status) => 
    api.get('/hcm/personnel-areas', { params: { company_code_id: companyCodeId, status } }),
  getPersonnelArea: (personnelAreaId) => api.get(`/hcm/personnel-areas/${personnelAreaId}`),
  createPersonnelArea: (data) => api.post('/hcm/personnel-areas', data),
  updatePersonnelArea: (personnelAreaId, data) => api.put(`/hcm/personnel-areas/${personnelAreaId}`, data),
  deletePersonnelArea: (personnelAreaId) => api.delete(`/hcm/personnel-areas/${personnelAreaId}`),
  
  // Personnel Subareas
  getPersonnelSubareas: (personnelAreaId, status) => 
    api.get('/hcm/personnel-subareas', { params: { personnel_area_id: personnelAreaId, status } }),
  getPersonnelSubarea: (personnelAreaId, personnelSubAreaId) => 
    api.get(`/hcm/personnel-subareas/${personnelAreaId}/${personnelSubAreaId}`),
  createPersonnelSubarea: (data) => api.post('/hcm/personnel-subareas', data),
  updatePersonnelSubarea: (personnelAreaId, personnelSubAreaId, data) => 
    api.put(`/hcm/personnel-subareas/${personnelAreaId}/${personnelSubAreaId}`, data),
  deletePersonnelSubarea: (personnelAreaId, personnelSubAreaId) => 
    api.delete(`/hcm/personnel-subareas/${personnelAreaId}/${personnelSubAreaId}`),
  
  // Employee Groups
  getEmployeeGroups: (status) => 
    api.get('/hcm/employee-groups', { params: { status } }),
  getEmployeeGroup: (employeeGroupId) => api.get(`/hcm/employee-groups/${employeeGroupId}`),
  createEmployeeGroup: (data) => api.post('/hcm/employee-groups', data),
  updateEmployeeGroup: (employeeGroupId, data) => api.put(`/hcm/employee-groups/${employeeGroupId}`, data),
  deleteEmployeeGroup: (employeeGroupId) => api.delete(`/hcm/employee-groups/${employeeGroupId}`),
  
  // Employee Subgroups (composite key: group_id + subgroup_id)
  getEmployeeSubgroups: (employeeGroupId, status) => 
    api.get('/hcm/employee-subgroups', { params: { employee_group_id: employeeGroupId, status } }),
  getEmployeeSubgroup: (employeeGroupId, employeeSubgroupId) => 
    api.get(`/hcm/employee-subgroups/${employeeGroupId}/${employeeSubgroupId}`),
  createEmployeeSubgroup: (data) => api.post('/hcm/employee-subgroups', data),
  updateEmployeeSubgroup: (employeeGroupId, employeeSubgroupId, data) => 
    api.put(`/hcm/employee-subgroups/${employeeGroupId}/${employeeSubgroupId}`, data),
  deleteEmployeeSubgroup: (employeeGroupId, employeeSubgroupId) => 
    api.delete(`/hcm/employee-subgroups/${employeeGroupId}/${employeeSubgroupId}`),

  // Pay Scale Types
  getPayScaleTypes: (status) => api.get('/hcm/pay-scale-types', { params: { status } }),
  getPayScaleType: (payScaleTypeId) => api.get(`/hcm/pay-scale-types/${payScaleTypeId}`),
  createPayScaleType: (data) => api.post('/hcm/pay-scale-types', data),
  updatePayScaleType: (payScaleTypeId, data) => api.put(`/hcm/pay-scale-types/${payScaleTypeId}`, data),

  // Pay Scale Areas
  getPayScaleAreas: (payScaleTypeId, status) => 
    api.get('/hcm/pay-scale-areas', { params: { pay_scale_type_id: payScaleTypeId, status } }),
  createPayScaleArea: (data) => api.post('/hcm/pay-scale-areas', data),
  updatePayScaleArea: (payScaleTypeId, payScaleAreaId, data) => 
    api.put(`/hcm/pay-scale-areas/${payScaleTypeId}/${payScaleAreaId}`, data),

  // Pay Scale Groups
  getPayScaleGroups: (payScaleTypeId, payScaleAreaId, status) => 
    api.get('/hcm/pay-scale-groups', { params: { pay_scale_type_id: payScaleTypeId, pay_scale_area_id: payScaleAreaId, status } }),
  createPayScaleGroup: (data) => api.post('/hcm/pay-scale-groups', data),
  updatePayScaleGroup: (payScaleTypeId, payScaleAreaId, payScaleGroupId, data) => 
    api.put(`/hcm/pay-scale-groups/${payScaleTypeId}/${payScaleAreaId}/${payScaleGroupId}`, data),

  // Pay Scale Levels
  getPayScaleLevels: (payScaleTypeId, payScaleAreaId, payScaleGroupId, status) => 
    api.get('/hcm/pay-scale-levels', { params: { pay_scale_type_id: payScaleTypeId, pay_scale_area_id: payScaleAreaId, pay_scale_group_id: payScaleGroupId, status } }),
  createPayScaleLevel: (data) => api.post('/hcm/pay-scale-levels', data),
  updatePayScaleLevel: (payScaleTypeId, payScaleAreaId, payScaleGroupId, payScaleLevelId, data) => 
    api.put(`/hcm/pay-scale-levels/${payScaleTypeId}/${payScaleAreaId}/${payScaleGroupId}/${payScaleLevelId}`, data),

  // Wage Types
  getWageTypes: (wageTypeClass, isRecurring, status) => 
    api.get('/hcm/wage-types', { params: { wage_type_class: wageTypeClass, is_recurring: isRecurring, status } }),
  getWageType: (wageTypeId) => api.get(`/hcm/wage-types/${wageTypeId}`),
  createWageType: (data) => api.post('/hcm/wage-types', data),
  updateWageType: (wageTypeId, data) => api.put(`/hcm/wage-types/${wageTypeId}`, data),
};

// Organizational Management APIs
export const omAPI = {
  // Org Units
  getOrgUnits: (params = {}) => api.get('/hcm/om/org-units', { params }),
  getOrgUnit: (orgUnitId) => api.get(`/hcm/om/org-units/${orgUnitId}`),
  createOrgUnit: (data) => api.post('/hcm/om/org-units', data),
  updateOrgUnit: (orgUnitId, data) => api.put(`/hcm/om/org-units/${orgUnitId}`, data),

  // Jobs
  getJobs: (params = {}) => api.get('/hcm/om/jobs', { params }),
  getJob: (jobId) => api.get(`/hcm/om/jobs/${jobId}`),
  createJob: (data) => api.post('/hcm/om/jobs', data),
  updateJob: (jobId, data) => api.put(`/hcm/om/jobs/${jobId}`, data),

  // Positions
  getPositions: (params = {}) => api.get('/hcm/om/positions', { params }),
  getPosition: (positionId) => api.get(`/hcm/om/positions/${positionId}`),
  createPosition: (data) => api.post('/hcm/om/positions', data),
  updatePosition: (positionId, data) => api.put(`/hcm/om/positions/${positionId}`, data),

  // Tasks
  getTasks: (params = {}) => api.get('/hcm/om/tasks', { params }),
  getTask: (taskId) => api.get(`/hcm/om/tasks/${taskId}`),
  createTask: (data) => api.post('/hcm/om/tasks', data),
  updateTask: (taskId, data) => api.put(`/hcm/om/tasks/${taskId}`, data),

  // Task Assignments
  getTaskAssignments: (params = {}) => api.get('/hcm/om/task-assignments', { params }),
  createTaskAssignment: (data) => api.post('/hcm/om/task-assignments', data),

  // Position Holders
  getPositionHolders: (params = {}) => api.get('/hcm/om/position-holders', { params }),
  createPositionHolder: (data) => api.post('/hcm/om/position-holders', data),
  updatePositionHolder: (holderId, data) => api.put(`/hcm/om/position-holders/${holderId}`, data),

  // Org Chart
  getOrgChart: (rootOrgUnitId) => api.get('/hcm/om/org-chart', { params: { root_org_unit_id: rootOrgUnitId } }),
};

// Time Management API
export const timeManagementAPI = {
  // Dashboard
  getDashboard: () => api.get('/hcm/time/dashboard'),

  // Holiday Calendars
  getHolidayCalendars: (params = {}) => api.get('/hcm/time/holiday-calendars', { params }),
  getHolidayCalendar: (id) => api.get(`/hcm/time/holiday-calendars/${id}`),
  createHolidayCalendar: (data) => api.post('/hcm/time/holiday-calendars', data),
  updateHolidayCalendar: (id, data) => api.put(`/hcm/time/holiday-calendars/${id}`, data),

  // Daily Work Schedules
  getDailyWorkSchedules: (params = {}) => api.get('/hcm/time/daily-work-schedules', { params }),
  getDailyWorkSchedule: (id) => api.get(`/hcm/time/daily-work-schedules/${id}`),
  createDailyWorkSchedule: (data) => api.post('/hcm/time/daily-work-schedules', data),
  updateDailyWorkSchedule: (id, data) => api.put(`/hcm/time/daily-work-schedules/${id}`, data),

  // Work Schedule Rules
  getWorkScheduleRules: (params = {}) => api.get('/hcm/time/work-schedule-rules', { params }),
  getWorkScheduleRule: (id) => api.get(`/hcm/time/work-schedule-rules/${id}`),
  createWorkScheduleRule: (data) => api.post('/hcm/time/work-schedule-rules', data),
  updateWorkScheduleRule: (id, data) => api.put(`/hcm/time/work-schedule-rules/${id}`, data),

  // Absence Types
  getAbsenceTypes: (params = {}) => api.get('/hcm/time/absence-types', { params }),
  getAbsenceType: (id) => api.get(`/hcm/time/absence-types/${id}`),
  createAbsenceType: (data) => api.post('/hcm/time/absence-types', data),
  updateAbsenceType: (id, data) => api.put(`/hcm/time/absence-types/${id}`, data),

  // Attendance Types
  getAttendanceTypes: (params = {}) => api.get('/hcm/time/attendance-types', { params }),
  getAttendanceType: (id) => api.get(`/hcm/time/attendance-types/${id}`),
  createAttendanceType: (data) => api.post('/hcm/time/attendance-types', data),
  updateAttendanceType: (id, data) => api.put(`/hcm/time/attendance-types/${id}`, data),

  // Absence Quota Types
  getAbsenceQuotaTypes: (params = {}) => api.get('/hcm/time/absence-quota-types', { params }),
  getAbsenceQuotaType: (id) => api.get(`/hcm/time/absence-quota-types/${id}`),
  createAbsenceQuotaType: (data) => api.post('/hcm/time/absence-quota-types', data),
  updateAbsenceQuotaType: (id, data) => api.put(`/hcm/time/absence-quota-types/${id}`, data),

  // Absences (IT2001)
  getAbsences: (params = {}) => api.get('/hcm/time/absences', { params }),
  getAbsence: (id) => api.get(`/hcm/time/absences/${id}`),
  createAbsence: (data) => api.post('/hcm/time/absences', data),
  updateAbsence: (id, data) => api.put(`/hcm/time/absences/${id}`, data),
  calculateAbsenceDays: (params) => api.get('/hcm/time/absences/calculate-days', { params }),
  checkWorkingDay: (params) => api.get('/hcm/time/check-working-day', { params }),

  // Attendances (IT2002)
  getAttendances: (params = {}) => api.get('/hcm/time/attendances', { params }),
  getAttendance: (id) => api.get(`/hcm/time/attendances/${id}`),
  createAttendance: (data) => api.post('/hcm/time/attendances', data),
  updateAttendance: (id, data) => api.put(`/hcm/time/attendances/${id}`, data),

  // Employee Quotas (IT2006)
  getEmployeeQuotas: (params = {}) => api.get('/hcm/time/employee-quotas', { params }),
  getEmployeeQuota: (id) => api.get(`/hcm/time/employee-quotas/${id}`),
  createEmployeeQuota: (data) => api.post('/hcm/time/employee-quotas', data),
  updateEmployeeQuota: (id, data) => api.put(`/hcm/time/employee-quotas/${id}`, data),
};

// Payroll API
export const payrollAPI = {
  // Dashboard
  getDashboard: () => api.get('/hcm/payroll/dashboard'),

  // Payroll Areas
  getPayrollAreas: (params = {}) => api.get('/hcm/payroll/areas', { params }),
  getPayrollArea: (id) => api.get(`/hcm/payroll/areas/${id}`),
  createPayrollArea: (data) => api.post('/hcm/payroll/areas', data),
  updatePayrollArea: (id, data) => api.put(`/hcm/payroll/areas/${id}`, data),

  // Payroll Periods
  getPayrollPeriods: (params = {}) => api.get('/hcm/payroll/periods', { params }),
  getPayrollPeriod: (id) => api.get(`/hcm/payroll/periods/${id}`),
  generatePeriods: (areaId, startYear, endYear) => 
    api.post(`/hcm/payroll/periods/generate?payroll_area_id=${areaId}&start_year=${startYear}&end_year=${endYear}`),
  updatePayrollPeriod: (id, data) => api.put(`/hcm/payroll/periods/${id}`, data),

  // Tax Configurations
  getTaxConfigs: (params = {}) => api.get('/hcm/payroll/tax-configs', { params }),
  getTaxConfig: (id) => api.get(`/hcm/payroll/tax-configs/${id}`),
  createTaxConfig: (data) => api.post('/hcm/payroll/tax-configs', data),
  updateTaxConfig: (id, data) => api.put(`/hcm/payroll/tax-configs/${id}`, data),

  // Social Security Configurations
  getSSConfigs: (params = {}) => api.get('/hcm/payroll/ss-configs', { params }),
  getSSConfig: (id) => api.get(`/hcm/payroll/ss-configs/${id}`),
  createSSConfig: (data) => api.post('/hcm/payroll/ss-configs', data),
  updateSSConfig: (id, data) => api.put(`/hcm/payroll/ss-configs/${id}`, data),

  // Payroll Runs
  getRuns: (params = {}) => api.get('/hcm/payroll/runs', { params }),
  getRun: (id) => api.get(`/hcm/payroll/runs/${id}`),
  createRun: (data) => api.post('/hcm/payroll/runs', data),
  updateRun: (id, data) => api.put(`/hcm/payroll/runs/${id}`, data),
  executeRun: (id, executedBy) => api.post(`/hcm/payroll/runs/${id}/execute?executed_by=${executedBy}`),
  approveRun: (id, approvedBy) => api.post(`/hcm/payroll/runs/${id}/approve?approved_by=${approvedBy}`),
  releaseForCorrection: (id) => api.post(`/hcm/payroll/runs/${id}/release-for-correction`),

  // Payroll Results
  getResults: (params = {}) => api.get('/hcm/payroll/results', { params }),
  getResult: (id) => api.get(`/hcm/payroll/results/${id}`),
  updateResult: (id, data) => api.put(`/hcm/payroll/results/${id}`, data),
  getPayslip: (resultId) => api.get(`/hcm/payroll/results/${resultId}/payslip`),

  // Bank Transfers
  getBankTransfers: (params = {}) => api.get('/hcm/payroll/bank-transfers', { params }),
  getBankTransfer: (id) => api.get(`/hcm/payroll/bank-transfers/${id}`),
  generateBankTransfer: (runId) => api.post(`/hcm/payroll/bank-transfers/generate/${runId}`),
  updateBankTransfer: (id, data) => api.put(`/hcm/payroll/bank-transfers/${id}`, data),
  submitBankTransfer: (id, submittedBy) => api.post(`/hcm/payroll/bank-transfers/${id}/submit?submitted_by=${submittedBy}`),
  markTransferProcessed: (id) => api.post(`/hcm/payroll/bank-transfers/${id}/mark-processed`),

  // GL Mappings (Finance Integration)
  getGLMappings: (params = {}) => api.get('/hcm/payroll/gl/mappings', { params }),
  getGLMapping: (id) => api.get(`/hcm/payroll/gl/mappings/${id}`),
  createGLMapping: (data) => api.post('/hcm/payroll/gl/mappings', data),
  updateGLMapping: (id, data) => api.put(`/hcm/payroll/gl/mappings/${id}`, data),

  // GL Postings (Finance Integration)
  getGLPostings: (params = {}) => api.get('/hcm/payroll/gl/postings', { params }),
  getGLPosting: (id) => api.get(`/hcm/payroll/gl/postings/${id}`),
  createGLPosting: (runId, postedBy = 'SYSTEM') => 
    api.post(`/hcm/payroll/gl/postings/${runId}?posted_by=${postedBy}`),
};

// Project Systems (PS) APIs
export const psAPI = {
  // Dashboard
  getDashboard: () => api.get('/ps/dashboard'),

  // Project Profiles
  getProjectProfiles: (params = {}) => api.get('/ps/profiles/project', { params }),
  getProjectProfile: (id) => api.get(`/ps/profiles/project/${id}`),
  createProjectProfile: (data) => api.post('/ps/profiles/project', data),
  updateProjectProfile: (id, data) => api.put(`/ps/profiles/project/${id}`, data),

  // WBS Profiles
  getWBSProfiles: (params = {}) => api.get('/ps/profiles/wbs', { params }),
  getWBSProfile: (id) => api.get(`/ps/profiles/wbs/${id}`),
  createWBSProfile: (data) => api.post('/ps/profiles/wbs', data),
  updateWBSProfile: (id, data) => api.put(`/ps/profiles/wbs/${id}`, data),

  // Network Profiles
  getNetworkProfiles: (params = {}) => api.get('/ps/profiles/network', { params }),
  getNetworkProfile: (id) => api.get(`/ps/profiles/network/${id}`),
  createNetworkProfile: (data) => api.post('/ps/profiles/network', data),
  updateNetworkProfile: (id, data) => api.put(`/ps/profiles/network/${id}`, data),

  // Status Profiles
  getStatusProfiles: (params = {}) => api.get('/ps/profiles/status', { params }),
  getStatusProfile: (id) => api.get(`/ps/profiles/status/${id}`),
  createStatusProfile: (data) => api.post('/ps/profiles/status', data),
  updateStatusProfile: (id, data) => api.put(`/ps/profiles/status/${id}`, data),

  // Planning Profiles
  getPlanningProfiles: (params = {}) => api.get('/ps/profiles/planning', { params }),
  getPlanningProfile: (id) => api.get(`/ps/profiles/planning/${id}`),
  createPlanningProfile: (data) => api.post('/ps/profiles/planning', data),
  updatePlanningProfile: (id, data) => api.put(`/ps/profiles/planning/${id}`, data),

  // Budget Profiles (Phase 2)
  getBudgetProfiles: (params = {}) => api.get('/ps/budget-profiles', { params }),
  getBudgetProfile: (id) => api.get(`/ps/budget-profiles/${id}`),
  createBudgetProfile: (data) => api.post('/ps/budget-profiles', data),
  updateBudgetProfile: (id, data) => api.put(`/ps/budget-profiles/${id}`, data),

  // Projects
  getProjects: (params = {}) => api.get('/ps/projects', { params }),
  getProject: (id) => api.get(`/ps/projects/${id}`),
  createProject: (data) => api.post('/ps/projects', data),
  updateProject: (id, data) => api.put(`/ps/projects/${id}`, data),
  getProjectHierarchy: (id) => api.get(`/ps/projects/${id}/hierarchy`),
  releaseProject: (id) => api.post(`/ps/projects/${id}/release`),
  completeProject: (id) => api.post(`/ps/projects/${id}/complete`),
  closeProject: (id) => api.post(`/ps/projects/${id}/close`),
  getProjectBudgetSummary: (projectId, fiscalYear) => api.get(`/ps/projects/${projectId}/budget-summary`, { params: { fiscal_year: fiscalYear } }),

  // WBS Elements
  getWBSElements: (params = {}) => api.get('/ps/wbs-elements', { params }),
  getWBSElement: (id) => api.get(`/ps/wbs-elements/${id}`),
  createWBSElement: (data) => api.post('/ps/wbs-elements', data),
  updateWBSElement: (id, data) => api.put(`/ps/wbs-elements/${id}`, data),
  releaseWBSElement: (id) => api.post(`/ps/wbs-elements/${id}/release`),

  // WBS Budget (Phase 2)
  getWBSBudget: (wbsElementId, fiscalYear) => api.get(`/ps/wbs-budgets/${wbsElementId}`, { params: { fiscal_year: fiscalYear } }),

  // Budget Documents (Phase 2)
  getBudgetDocuments: (params = {}) => api.get('/ps/budget-documents', { params }),
  getBudgetDocument: (documentNumber) => api.get(`/ps/budget-documents/${documentNumber}`),
  createBudgetDocument: (data) => api.post('/ps/budget-documents', data),
  updateBudgetDocument: (documentNumber, data) => api.put(`/ps/budget-documents/${documentNumber}`, data),
  releaseBudgetDocument: (documentNumber, releaseAmount = null) => 
    api.post(`/ps/budget-documents/${documentNumber}/release`, null, { params: releaseAmount ? { release_amount: releaseAmount } : {} }),

  // Cost Plans (Phase 2)
  getCostPlans: (params = {}) => api.get('/ps/cost-plans', { params }),
  getCostPlan: (planId) => api.get(`/ps/cost-plans/${planId}`),
  createCostPlan: (data) => api.post('/ps/cost-plans', data),
  updateCostPlan: (planId, data) => api.put(`/ps/cost-plans/${planId}`, data),
  activateCostPlan: (planId) => api.post(`/ps/cost-plans/${planId}/activate`),

  // Revenue Plans (Phase 2)
  getRevenuePlans: (params = {}) => api.get('/ps/revenue-plans', { params }),
  getRevenuePlan: (planId) => api.get(`/ps/revenue-plans/${planId}`),
  createRevenuePlan: (data) => api.post('/ps/revenue-plans', data),
  updateRevenuePlan: (planId, data) => api.put(`/ps/revenue-plans/${planId}`, data),

  // Commitments (Phase 2)
  getCommitments: (params = {}) => api.get('/ps/commitments', { params }),
  getCommitment: (commitmentId) => api.get(`/ps/commitments/${commitmentId}`),
  createCommitment: (data) => api.post('/ps/commitments', data),
  updateCommitment: (commitmentId, data) => api.put(`/ps/commitments/${commitmentId}`, data),
  cancelCommitment: (commitmentId) => api.post(`/ps/commitments/${commitmentId}/cancel`),

  // Actual Costs (Phase 2)
  getActualCosts: (params = {}) => api.get('/ps/actual-costs', { params }),
  createActualCost: (data) => api.post('/ps/actual-costs', data),

  // Availability Check (Phase 2)
  checkAvailability: (data) => api.post('/ps/availability-check', data),

  // Plan/Actual Comparison (Phase 2)
  getPlanActualComparison: (data) => api.post('/ps/plan-actual-comparison', data),

  // Budget Carryforward (Phase 2)
  carryforwardBudget: (data) => api.post('/ps/budget-carryforward', data),

  // Planning Dashboard (Phase 2)
  getPlanningDashboard: (fiscalYear = null) => api.get('/ps/planning-dashboard', { params: fiscalYear ? { fiscal_year: fiscalYear } : {} }),

  // Templates
  getTemplates: (params = {}) => api.get('/ps/templates', { params }),
  getTemplate: (id) => api.get(`/ps/templates/${id}`),
  createTemplate: (data) => api.post('/ps/templates', data),
  updateTemplate: (id, data) => api.put(`/ps/templates/${id}`, data),
  createProjectFromTemplate: (templateId, data) => api.post(`/ps/templates/${templateId}/create-project`, data),

  // Phase 3: Networks
  getNetworks: (params = {}) => api.get('/ps/networks', { params }),
  getNetwork: (id) => api.get(`/ps/networks/${id}`),
  createNetwork: (data) => api.post('/ps/networks', data),
  updateNetwork: (id, data) => api.put(`/ps/networks/${id}`, data),
  releaseNetwork: (id) => api.post(`/ps/networks/${id}/release`),

  // Phase 3: Activities
  getActivities: (params = {}) => api.get('/ps/activities', { params }),
  getActivity: (id) => api.get(`/ps/activities/${id}`),
  createActivity: (data) => api.post('/ps/activities', data),
  updateActivity: (id, data) => api.put(`/ps/activities/${id}`, data),
  releaseActivity: (id) => api.post(`/ps/activities/${id}/release`),

  // Phase 3: Confirmations
  getConfirmations: (params = {}) => api.get('/ps/confirmations', { params }),
  getConfirmation: (id) => api.get(`/ps/confirmations/${id}`),
  createConfirmation: (data) => api.post('/ps/confirmations', data),
  cancelConfirmation: (id) => api.post(`/ps/confirmations/${id}/cancel`),

  // Phase 3: Material Reservations
  getMaterialReservations: (params = {}) => api.get('/ps/material-reservations', { params }),
  getMaterialReservation: (id) => api.get(`/ps/material-reservations/${id}`),
  createMaterialReservation: (data) => api.post('/ps/material-reservations', data),
  updateMaterialReservation: (id, data) => api.put(`/ps/material-reservations/${id}`, data),

  // Phase 3: Goods Issues
  getGoodsIssues: (params = {}) => api.get('/ps/goods-issues', { params }),
  getGoodsIssue: (id) => api.get(`/ps/goods-issues/${id}`),
  createGoodsIssue: (data) => api.post('/ps/goods-issues', data),

  // Phase 3: Progress
  updateProgress: (data) => api.post('/ps/progress/update', data),
  getProjectProgress: (projectId) => api.get(`/ps/projects/${projectId}/progress`),

  // Phase 3: Execution Dashboard
  getExecutionDashboard: () => api.get('/ps/execution-dashboard'),

  // Phase 4: Period Locks
  getPeriodLocks: (params = {}) => api.get('/ps/period-locks', { params }),
  getPeriodLock: (id) => api.get(`/ps/period-locks/${id}`),
  createPeriodLock: (data) => api.post('/ps/period-locks', data),
  unlockPeriod: (id) => api.post(`/ps/period-locks/${id}/unlock`),
  closePeriod: (id) => api.post(`/ps/period-locks/${id}/close`),

  // Phase 4: WIP Calculation
  calculateWIP: (data) => api.post('/ps/wip-calculation', data),

  // Phase 4: Settlement
  getSettlementRules: (params = {}) => api.get('/ps/settlement-rules', { params }),
  getSettlementRule: (id) => api.get(`/ps/settlement-rules/${id}`),
  createSettlementRule: (data) => api.post('/ps/settlement-rules', data),
  updateSettlementRule: (id, data) => api.put(`/ps/settlement-rules/${id}`, data),
  executeSettlement: (data) => api.post('/ps/settlements/execute', data),
  getSettlements: (params = {}) => api.get('/ps/settlements', { params }),
  getSettlement: (id) => api.get(`/ps/settlements/${id}`),

  // Phase 4: Project Closure
  checkProjectClosure: (data) => api.post('/ps/projects/closure-check', data),
  closeProject: (data) => api.post('/ps/projects/close', data),

  // Phase 4: Reports
  getProjectStatusReport: (data) => api.post('/ps/reports/project-status', data),
  getProjectCostReport: (data) => api.post('/ps/reports/project-cost', data),
  getProjectTimeline: (projectId) => api.get(`/ps/reports/project-timeline/${projectId}`),
  rescheduleTimelineItem: (data) => api.patch('/ps/reports/timeline/reschedule', data),
  exportProjectStatusReport: (data, format) =>
    api.post(`/ps/reports/project-status/export?format=${format}`, data, { responseType: 'blob' }),
  exportProjectCostReport: (data, format) =>
    api.post(`/ps/reports/project-cost/export?format=${format}`, data, { responseType: 'blob' }),
  getClosingDashboard: () => api.get('/ps/closing-dashboard'),

  // PS-FI Integration
  // Account Determinations
  getAccountDeterminations: (params = {}) => api.get('/ps/fi/account-determinations', { params }),
  getAccountDetermination: (id) => api.get(`/ps/fi/account-determinations/${id}`),
  createAccountDetermination: (data) => api.post('/ps/fi/account-determinations', data),
  updateAccountDetermination: (id, data) => api.put(`/ps/fi/account-determinations/${id}`, data),
  
  // FI Documents
  getFIDocuments: (params = {}) => api.get('/ps/fi/documents', { params }),
  getFIDocument: (id) => api.get(`/ps/fi/documents/${id}`),
  reverseFIDocument: (id, reversalDate, reversalReason = null) => {
    const params = { reversal_date: reversalDate };
    if (reversalReason) params.reversal_reason = reversalReason;
    return api.post(`/ps/fi/documents/${id}/reverse`, null, { params });
  },
  
  // WIP Posting
  postWIPToFI: (data) => api.post('/ps/fi/post-wip', data),
  
  // Settlement Posting
  postSettlementToFI: (settlementDocumentId) => api.post(`/ps/fi/post-settlement/${settlementDocumentId}`),
  
  // PS-FI Dashboard
  getPSFIDashboard: () => api.get('/ps/fi/dashboard'),
};

// Investment Programs (capital budget hierarchy)
export const investmentApi = {
  listPrograms: () => api.get('/investment-programs'),
  getProgram: (id) => api.get(`/investment-programs/${id}`),
  createProgram: (data) => api.post('/investment-programs', data),
  updateProgram: (id, data) => api.put(`/investment-programs/${id}`, data),
  deleteProgram: (id) => api.delete(`/investment-programs/${id}`),
  getRollup: (id) => api.get(`/investment-programs/${id}/rollup`),
  listPositions: (id) => api.get(`/investment-programs/${id}/positions`),
  createPosition: (id, data) => api.post(`/investment-programs/${id}/positions`, data),
  updatePosition: (positionId, data) => api.put(`/investment-programs/positions/${positionId}`, data),
  deletePosition: (positionId) => api.delete(`/investment-programs/positions/${positionId}`),
  listAssignments: (positionId) => api.get(`/investment-programs/positions/${positionId}/assignments`),
  createAssignment: (positionId, data) => api.post(`/investment-programs/positions/${positionId}/assignments`, data),
  deleteAssignment: (assignmentId) => api.delete(`/investment-programs/assignments/${assignmentId}`),
};

// P2P Document Number Ranges (config)
export const numberRangesApi = {
  list: (documentType) => api.get('/p2p-number-ranges', { params: documentType ? { document_type: documentType } : {} }),
  create: (data) => api.post('/p2p-number-ranges', data),
  update: (rangeId, documentType, data) => api.put(`/p2p-number-ranges/${rangeId}/${documentType}`, data),
};

// Manufacturing (PP) Module
const MFG_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/manufacturing`;
export const mfgAPI = {
  // Dashboard
  getDashboard: () => api.get('/manufacturing/dashboard'),

  // Work Centers
  getWorkCenters: (params = {}) => api.get('/manufacturing/work-centers', { params }),
  getWorkCenter: (id) => api.get(`/manufacturing/work-centers/${id}`),
  createWorkCenter: (data) => api.post('/manufacturing/work-centers', data),
  updateWorkCenter: (id, data) => api.put(`/manufacturing/work-centers/${id}`, data),
  deleteWorkCenter: (id) => api.delete(`/manufacturing/work-centers/${id}`),

  // Bill of Materials
  getBOMs: (params = {}) => api.get('/manufacturing/boms', { params }),
  getBOM: (id) => api.get(`/manufacturing/boms/${id}`),
  createBOM: (data) => api.post('/manufacturing/boms', data),
  updateBOM: (id, data) => api.put(`/manufacturing/boms/${id}`, data),
  deleteBOM: (id) => api.delete(`/manufacturing/boms/${id}`),

  // Routings
  getRoutings: (params = {}) => api.get('/manufacturing/routings', { params }),
  getRouting: (id) => api.get(`/manufacturing/routings/${id}`),
  createRouting: (data) => api.post('/manufacturing/routings', data),
  updateRouting: (id, data) => api.put(`/manufacturing/routings/${id}`, data),
  deleteRouting: (id) => api.delete(`/manufacturing/routings/${id}`),

  // Production Orders
  getProductionOrders: (params = {}) => api.get('/manufacturing/production-orders', { params }),
  getProductionOrder: (id) => api.get(`/manufacturing/production-orders/${id}`),
  createProductionOrder: (data) => api.post('/manufacturing/production-orders', data),
  updateProductionOrder: (id, data) => api.put(`/manufacturing/production-orders/${id}`, data),
  releaseProductionOrder: (id) => api.post(`/manufacturing/production-orders/${id}/release`),
  tecoProductionOrder: (id) => api.post(`/manufacturing/production-orders/${id}/teco`),
  closeProductionOrder: (id) => api.post(`/manufacturing/production-orders/${id}/close`),
  deleteProductionOrder: (id) => api.delete(`/manufacturing/production-orders/${id}`),
  exportProductionOrderUrl: (id, format) => `${MFG_BASE}/production-orders/${id}/export?format=${format}`,

  // Confirmations
  getConfirmations: (orderId) => api.get('/manufacturing/confirmations', { params: orderId ? { order_id: orderId } : {} }),
  createConfirmation: (data) => api.post('/manufacturing/confirmations', data),

  // Number Ranges
  getNumberRanges: () => api.get('/manufacturing/number-ranges'),
  createNumberRange: (data) => api.post('/manufacturing/number-ranges', data),
  updateNumberRange: (documentType, data) => api.put(`/manufacturing/number-ranges/${documentType}`, data),
  deleteNumberRange: (documentType) => api.delete(`/manufacturing/number-ranges/${documentType}`),

  // Shared master-data lookups (dropdown sources)
  getMaterials: () => api.get('/materials'),
  getUOMs: () => api.get('/units-of-measure'),
  getPlants: () => api.get('/material-management/plants'),
};
